import React from 'react';
import { MdHub } from 'react-icons/md';
import styles from './KPICard.module.css';

type IconType = 'federation' | 'queries' | 'engaged' | 'transfer';

const ICONS: Record<IconType, React.ReactNode> = {
  federation: <MdHub size={16} />,
  queries: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  ),
  engaged: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  transfer: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  ),
};

interface KPICardProps {
  value: string | number;
  rawValue?: number;
  title: string;
  accentColor: string;
  iconType?: IconType;
  isLoading?: boolean;
  tooltip?: string;
}

const KPICard: React.FC<KPICardProps> = ({ value, rawValue, title, accentColor, iconType, isLoading, tooltip }) => {
  const cardStyle = {
    borderTopColor: accentColor,
    background: `linear-gradient(135deg, #ffffff 55%, ${accentColor}12 100%)`,
  };

  if (isLoading) {
    return (
      <div className={styles.card} style={cardStyle}>
        <div className={`${styles.shimmerBlock} ${styles.shimmerLabel}`} />
        <div className={`${styles.shimmerBlock} ${styles.shimmerValue}`} />
      </div>
    );
  }

  return (
    <div className={styles.card} style={cardStyle}>
      <div className={styles.cardHeader}>
        <span className={styles.titleRow}>
          <span className={styles.title}>{title}</span>
          {tooltip && (
            <span className={styles.infoWrap}>
              <svg className={styles.infoIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span className={styles.infoTip}>{tooltip}</span>
            </span>
          )}
        </span>
        {iconType && (
          <span className={styles.icon} style={{ color: accentColor }}>
            {ICONS[iconType]}
          </span>
        )}
      </div>
      {rawValue !== undefined ? (
        <span className={styles.valueWrap}>
          <span className={styles.value}>{value}</span>
          <span className={styles.valueTip}>{rawValue.toLocaleString()}</span>
        </span>
      ) : (
        <span className={styles.value}>{value}</span>
      )}
    </div>
  );
};

export default KPICard;
